import * as React from "react";
import { ITheme } from "@brite-inc/ui-remote-transport-lms";
export { generateThemeColors } from "@brite-inc/theme-color/lib/generate_theme_colors";

export interface IUseTheme {
    themes: ITheme[];
    initialTheme?: ITheme;
}

export interface IUseThemeResult {
    selectedTheme: ITheme;
    setTheme: (theme: ITheme) => void;
}

export interface IThemeInput {
    primary: string;
    secondary: string;
    tertiary: string;
}

const ThemeQueryColorsLength = 11;
const ThemeQueryLogoUrlIndex = 11;

/** Default theme will mostly be used for the login page. */
const FallbackTheme: ITheme = {
    faviconUrl:
        "https://s3-us-west-2.amazonaws.com/brite-aws-beta-assets/themes/default/favicon.ico",
    id: "default",
    name: "default",
    logoUrl:
        "https://s3-us-west-2.amazonaws.com/brite-aws-beta-assets/themes/default/logo.png",
    primary: "#05cacc",
    primaryLight: "#08a9f2",
    primaryHover: "#18a4a5",
    primaryAlt: "#08a9f2",
    secondary: "#8032ed",
    secondaryLight: "#9156ec",
    secondaryHover: "#46128f",
    secondaryAlt: "#1b0a33",
    tertiary: "#ff6692",
    tertiaryLight: "#fd91b0",
    tertiaryHover: "#b3318f",
};

export function useTheme({ themes, initialTheme }: IUseTheme): IUseThemeResult {
    let theme = FallbackTheme;

    if (initialTheme) {
        theme = initialTheme;
    } else if (themes && themes[0]) {
        theme = themes[0];
    }

    const [selectedTheme, setSelectedTheme] = React.useState<ITheme>(theme);

    const setTheme = (theme: ITheme) => {
        setSelectedTheme(theme);

        Object.entries(theme).forEach(([variableName, value]) => {
            // @ts-ignore
            document.documentElement.style.setProperty(
                `--${variableName}`,
                value,
            );
        });
    };

    return {
        selectedTheme,
        setTheme,
    };
}

export const generateThemeFromThemeQueryString = (
    themeQuery: string,
    logoFallbackUrl: string,
): ITheme | null => {
    const themeQueryParams = themeQuery.split(",");
    const colors = themeQueryParams.slice(0, ThemeQueryColorsLength);

    if (colors.length !== ThemeQueryColorsLength) {
        return null;
    }

    const logo = themeQueryParams[ThemeQueryLogoUrlIndex];
    const logoUrl = logo?.length > 0 ? logo : logoFallbackUrl;

    const theme: ITheme = {
        faviconUrl: "/favicon.ico",
        id: "custom",
        name: "Custom",
        primary: "#" + colors[0],
        primaryAlt: "#" + colors[1],
        primaryHover: "#" + colors[2],
        primaryLight: "#" + colors[3],
        secondary: "#" + colors[4],
        secondaryAlt: "#" + colors[5],
        secondaryHover: "#" + colors[6],
        secondaryLight: "#" + colors[7],
        tertiary: "#" + colors[8],
        tertiaryHover: "#" + colors[9],
        tertiaryLight: "#" + colors[10],
        logoUrl,
    };

    return theme;
};

export const generateThemeQueryString = (
    theme?: ITheme,
    logoUrl?: string,
    avatarUrl?: string,
) => {
    if (!theme) {
        return null;
    }

    let query = "&theme=";

    query += theme.primary + ",";
    query += theme.primaryAlt + ",";
    query += theme.primaryHover + ",";
    query += theme.primaryLight + ",";
    query += theme.secondary + ",";
    query += theme.secondaryAlt + ",";
    query += theme.secondaryHover + ",";
    query += theme.secondaryLight + ",";
    query += theme.tertiary + ",";
    query += theme.tertiaryHover + ",";
    query += theme.tertiaryLight + ",";
    query = query.replace(/#/g, "");
    query += (logoUrl ?? "") + ",";
    query += avatarUrl ?? "";

    return query;
};
